import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getCurrentUser, getToken } from "@/lib/utils";
import { AUTH_ROUTE_PATHS } from "@/pages/Auth/authConfig";
import { getPendingJourneyPath } from "@/pages/Auth/signUpJourney";

interface ConditionalRouteProps {
  component: React.ComponentType;
  isPrivate?: boolean;
}

const ConditionalRoute: React.FC<ConditionalRouteProps> = ({
  component: Component,
  isPrivate = false,
}) => {
  const isAuthenticated = !!getToken();
  const location = useLocation();
  const authFlowPaths: string[] = [
    AUTH_ROUTE_PATHS.signUp,
    AUTH_ROUTE_PATHS.welcome,
    AUTH_ROUTE_PATHS.questions,
    AUTH_ROUTE_PATHS.introduction,
  ];
  const pendingJourneyPath = getPendingJourneyPath(
    getCurrentUser("isProfileUpdated"),
    getCurrentUser("email"),
  );
  const isAuthFlowPath = authFlowPaths.includes(location.pathname);

  const user = getCurrentUser();
  const isAdmin = Boolean(user?.isAdmin || user?.role === "ADMIN");

  if (isPrivate && !isAuthenticated) {
    const isAdminRoute = location.pathname.startsWith("/admin");
    return (
      <Navigate
        replace
        to={isAdminRoute ? AUTH_ROUTE_PATHS.adminLogin : AUTH_ROUTE_PATHS.signIn}
      />
    );
  }

  if (isPrivate && isAuthenticated) {
    if (isAdmin && !location.pathname.startsWith("/admin/users-management") && !location.pathname.startsWith("/users-management")) {
      return <Navigate replace to="/admin/users-management" />;
    }
    if (!isAdmin && (location.pathname.startsWith("/admin/users-management") || location.pathname.startsWith("/users-management"))) {
      return <Navigate replace to="/home" />;
    }
    if (!isAdmin && pendingJourneyPath && pendingJourneyPath !== location.pathname) {
      return <Navigate replace to={pendingJourneyPath} />;
    }
  }

  if (!isPrivate && isAuthenticated) {
    if (isAdmin) {
      return <Navigate replace to="/admin/users-management" />;
    }

    if (pendingJourneyPath) {
      if (pendingJourneyPath !== location.pathname) {
        return <Navigate replace to={pendingJourneyPath} />;
      }

      return <Component />;
    }

    if (isAuthFlowPath) {
      return <Navigate replace to="/home" />;
    }
  }

  return <Component />;
};

export default ConditionalRoute;
